'use client';

import { Navigation } from '@/components/Navigation';
import { DashboardBottomNav } from '@/components/dashboard/DashboardBottomNav';
import { GlobalHeader } from '@/components/GlobalHeader';
import { CommandPaletteV2 } from '@/components/CommandPaletteV2';
import { ToastProvider } from '@/components/ToastProvider';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { UIProvider, useUI } from '@/components/UIProvider';
import { NavigationProgress } from '@/components/NavigationProgress';

function LayoutContent({ children }: { children: React.ReactNode }) {
  const { isMobileSimulatorEnabled } = useUI();

  return (
    <div className="flex h-screen bg-bg-base overflow-hidden">
      <Navigation />
      <main id="main-content" className={`flex-1 overflow-hidden min-w-0 flex flex-col transition-colors duration-300 ${isMobileSimulatorEnabled ? 'items-center justify-center bg-zinc-950/50 p-8' : ''}`}>
        <div className={`flex flex-col h-full w-full transition-colors duration-300 ${
          isMobileSimulatorEnabled
            ? 'max-w-[375px] max-h-[812px] border-[12px] border-zinc-800 rounded-[3rem] shadow-2xl overflow-hidden bg-bg-base relative force-mobile'
            : ''
        }`}>
          {isMobileSimulatorEnabled && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-zinc-800 rounded-b-2xl z-50 flex items-center justify-center">
              <div className="w-10 h-1 bg-zinc-700 rounded-full" />
            </div>
          )}
          <GlobalHeader />
          <div className="flex-1 overflow-y-auto pb-14 md:pb-0">
            {children}
          </div>
        </div>
      </main>
      <DashboardBottomNav />
      <CommandPaletteV2 />
    </div>
  );
}

/**
 * Client-side dashboard shell. Renders the full navigation chrome.
 * Auth is handled by the server layout BEFORE this component mounts,
 * so by the time this renders the user is authenticated.
 */
export default function DashboardShell({ children }: { children: React.ReactNode }) {
  return (
    <UIProvider>
      <ToastProvider>
        <ImpersonationBanner />
        <NavigationProgress />
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded">Skip to content</a>
        <LayoutContent>{children}</LayoutContent>
      </ToastProvider>
    </UIProvider>
  );
}
